import { devLog, alwaysLog } from '@/utils/logger';

export type ObjectKey = string; // driveid 或 contentDigest + variant

interface CacheEntry {
  blob: Blob;
  objectURL: string;
  refCount: number;
  lastAccessAt: number;
  isValidated?: boolean; // 新增：验证状态缓存
}

interface CacheConfig {
  ramBudgetMB: number;
  entryTtlMs: number;
  validationCacheTtlMs?: number; // 新增：验证缓存TTL
  enableValidationCache?: boolean; // 新增：是否启用验证缓存
}

class BlobUrlCache {
  private cache = new Map<ObjectKey, CacheEntry>();
  private validationCache = new Map<string, { isValid: boolean; timestamp: number }>(); // 新增：验证结果缓存
  private config: CacheConfig = {
    ramBudgetMB: 384, // 默认 384MB
    entryTtlMs: 120_000, // 默认 2 分钟
    validationCacheTtlMs: 60_000, // 默认 1 分钟验证缓存
    enableValidationCache: true // 默认启用验证缓存
  };
  
  // 🎯 新增：防抖机制相关属性
  private lastCleanupTime = 0;
  private cleanupDebounceMs = 2000; // 2秒防抖
  // 🎯 新增：保护检查回调
  private protectionCallback: ((key: string) => boolean) | null = null;

  constructor() {
    // 尝试从配置文件加载
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { CacheConfig } = require('@/config/CacheConfig');
      this.setLimits(CacheConfig.blobUrlCache);
    } catch (e) {
      devLog.warn('[BlobUrlCache] 无法加载配置文件，使用默认配置');
    }
  }

  /**
   * 🎯 新增：设置保护检查回调
   * 允许外部（如PreloadScheduler）注册回调来阻止特定缓存项被清理
   */
  setProtectionCallback(callback: (key: string) => boolean): void {
    this.protectionCallback = callback;
    devLog.log('[BlobUrlCache] 保护检查回调已注册');
  }

  /**
   * 获取对象 URL，如果不存在则通过 factory 创建
   */
  async acquire(key: ObjectKey, factory: () => Promise<Blob>): Promise<{ url: string; release: () => void }> {
    // 检查缓存
    const entry = this.cache.get(key);
    if (entry) {
      entry.refCount++;
      entry.lastAccessAt = Date.now();
      devLog.log(`[BlobUrlCache] 命中缓存: ${key}, refCount: ${entry.refCount}`);
      return {
        url: entry.objectURL,
        release: () => this.release(key)
      };
    }

    // 创建新的 blob 和 URL
    devLog.log(`[BlobUrlCache] 创建新缓存: ${key}`);
    const blob = await factory();
    const objectURL = URL.createObjectURL(blob);
    
    const newEntry: CacheEntry = {
      blob,
      objectURL,
      refCount: 1,
      lastAccessAt: Date.now()
    };
    
    this.cache.set(key, newEntry);
    // 🎯 移除：不再在创建缓存时自动触发清理，改为在特定时机触发
    
    return {
      url: objectURL,
      release: () => this.release(key)
    };
  }

  /**
   * 🆕 直接创建URL（兼容ObjectUrlManager的createObjectURL方法）
   * @param blob 已存在的Blob对象
   * @param key 可选的缓存键，如果不提供则自动生成
   * @param autoRelease 是否自动释放（默认false）
   * @returns ObjectURL
   */
  createObjectURL(blob: Blob, key?: string, autoRelease = false): string {
    const cacheKey = key || `direct-${Date.now()}-${Math.random().toString(36)
.substr(2, 9)}`;
    
    // 检查是否已存在相同的blob
    const existingEntry = Array.from(this.cache.entries()).find(([_, entry]) => entry.blob === blob
    );
    
    if (existingEntry) {
      // 如果已存在，增加引用计数
      existingEntry[1].refCount++;
      existingEntry[1].lastAccessAt = Date.now();
      devLog.log(`[BlobUrlCache] 复用现有URL: ${cacheKey}, refCount: ${existingEntry[1].refCount}`);
      return existingEntry[1].objectURL;
    }
    
    // 创建新的URL
    const objectURL = URL.createObjectURL(blob);
    const newEntry: CacheEntry = {
      blob,
      objectURL,
      refCount: 1,
      lastAccessAt: Date.now()
    };
    
    this.cache.set(cacheKey, newEntry);
    // 🎯 移除：不再在创建缓存时自动触发清理，改为在特定时机触发
    
    devLog.log(`[BlobUrlCache] 直接创建URL: ${cacheKey}`);
    
    // 如果设置了自动释放，延迟释放
    if (autoRelease) {
      setTimeout(() => {
        this.release(cacheKey);
      }, 5000); // 5秒后自动释放
    }
    
    return objectURL;
  }

  /**
   * 验证blob URL是否有效（优化版本）
   */
  async isBlobUrlValid(blobUrl: string): Promise<boolean> {
    // 检查验证缓存
    if (this.config.enableValidationCache) {
      const cached = this.validationCache.get(blobUrl);
      if (cached && (Date.now() - cached.timestamp) < (this.config.validationCacheTtlMs ?? 300000)) { // 延长到5分钟
        return cached.isValid;
      }
    }

    let isValid = false;
    
    try {
      // 优化：对于blob URL，首先检查是否在当前缓存中存在
      const cacheEntry = Array.from(this.cache.values()).find(entry => entry.objectURL === blobUrl);
      if (cacheEntry) {
        // 如果在缓存中找到，说明是有效的
        isValid = true;
      } else {
        // 否则使用轻量级验证：尝试创建一个Image对象来测试blob URL
        isValid = await this.validateBlobUrlWithImage(blobUrl);
      }
      
      // 缓存验证结果
      if (this.config.enableValidationCache) {
        this.validationCache.set(blobUrl, {
          isValid,
          timestamp: Date.now()
        });
      }
      
      return isValid;
    } catch (error) {
      // 缓存失败结果
      if (this.config.enableValidationCache) {
        this.validationCache.set(blobUrl, {
          isValid: false,
          timestamp: Date.now()
        });
      }
      
      return false;
    }
  }

  /**
   * 使用Image对象验证blob URL（更轻量级的方法）
   */
  private validateBlobUrlWithImage(blobUrl: string): Promise<boolean> {
    return new Promise(resolve => {
      const img = new Image();
      const timeout = setTimeout(() => {
        resolve(false);
      }, 1000); // 1秒超时

      img.onload = () => {
        clearTimeout(timeout);
        resolve(true);
      };
      
      img.onerror = () => {
        clearTimeout(timeout);
        // 如果不是图片，尝试用fetch的方式
        this.validateBlobUrlWithFetch(blobUrl).then(resolve)
.catch(() => resolve(false));
      };

      img.src = blobUrl;
    });
  }

  /**
   * 使用fetch验证blob URL（备用方法）
   */
  private async validateBlobUrlWithFetch(blobUrl: string): Promise<boolean> {
    try {
      const response = await fetch(blobUrl, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * 验证URL是否有效（支持blob URL和其他URL）
   */
  async validateUrl(url: string): Promise<boolean> {
    // 对于blob URL，使用专门的验证方法
    if (url.startsWith('blob:')) {
      return this.isBlobUrlValid(url);
    }
    
    // 对于其他URL，使用fetch验证
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      devLog.warn(`[BlobUrlCache] 验证URL失败: ${url}`, error);
      return false;
    }
  }

  /**
   * 清理验证缓存
   */
  clearValidationCache(): void {
    this.validationCache.clear();
    devLog.log('[BlobUrlCache] 验证缓存已清理');
  }

  /**
   * 清理过期的验证缓存
   */
  private cleanupValidationCache(): void {
    if (!this.config.enableValidationCache) return;
    
    const now = Date.now();
    const toRemove: string[] = [];
    
    for (const [url, entry] of this.validationCache) {
      if ((now - entry.timestamp) > (this.config.validationCacheTtlMs ?? 60000)) {
        toRemove.push(url);
      }
    }
    
    for (const url of toRemove) {
      this.validationCache.delete(url);
    }
    
    if (toRemove.length > 0) {
      devLog.log(`[BlobUrlCache] 清理了 ${toRemove.length} 个过期的验证缓存`);
    }
  }

  /**
   * 更新访问时间
   */
  touch(key: ObjectKey): void {
    const entry = this.cache.get(key);
    if (entry) {
      entry.lastAccessAt = Date.now();
    }
  }

  /**
   * 🎯 新增：公开的释放方法
   */
  public releaseKey(key: ObjectKey): void {
    this.release(key);
  }

  /**
   * 释放引用
   */
  private release(key: ObjectKey): void {
    const entry = this.cache.get(key);
    if (!entry) return;

    entry.refCount--;
    devLog.log(`[BlobUrlCache] 释放引用: ${key}, refCount: ${entry.refCount}`);

    if (entry.refCount <= 0) {
      // 引用计数为 0，标记为可清理
      this.scheduleCleanup(key);
    }
  }

  /**
   * 设置限制
   */
  setLimits(config: Partial<CacheConfig>): void {
    Object.assign(this.config, config);
    devLog.log(`[BlobUrlCache] 配置更新:`, this.config);
  }

  /**
   * 清理所有缓存
   */
  async clearAll(): Promise<void> {
    devLog.log(`[BlobUrlCache] 清理所有缓存，共 ${this.cache.size} 项`);
    
    for (const [key, entry] of this.cache) {
      URL.revokeObjectURL(entry.objectURL);
    }
    this.cache.clear();
    
    // 同时清理验证缓存
    this.clearValidationCache();
  }

  /**
   * 获取缓存统计
   */
  getStats(): { size: number; totalRefs: number; totalSize: number } {
    let totalRefs = 0;
    let totalSize = 0;
    
    for (const entry of this.cache.values()) {
      totalRefs += entry.refCount;
      totalSize += entry.blob.size;
    }
    
    return {
      size: this.cache.size,
      totalRefs,
      totalSize
    };
  }

  /** 相对 ramBudgetMB 的用量比例（0~1+）；供预加载调度做预算门控 */
  getUsageRatio(): number {
    const maxBytes = this.config.ramBudgetMB * 1024 * 1024;
    if (maxBytes <= 0) return 0;
    return this.getStats().totalSize / maxBytes;
  }

  /**
   * 🎯 新增：获取缓存项列表（用于缓存监视）
   */
  getCacheItems(): Array<{
    id: string;
    cacheKey: string;
    size: number;
    refCount: number;
    lastAccessAt: number;
    type: 'audio' | 'video' | 'media' | 'other';
    driveId?: string;
    timeRange?: string;
  }> {
    const items: Array<{
      id: string;
      cacheKey: string;
      size: number;
      refCount: number;
      lastAccessAt: number;
      type: 'audio' | 'video' | 'media' | 'other';
      driveId?: string;
      timeRange?: string;
    }> = [];

    for (const [cacheKey, entry] of this.cache) {
      // 解析缓存键与MIME来确定类型和信息
      const type = this.inferCacheType(cacheKey, entry);
      const { driveId, timeRange } = this.parseCacheKeyInfo(cacheKey);

      items.push({
        id: cacheKey,
        cacheKey,
        size: entry.blob.size,
        refCount: entry.refCount,
        lastAccessAt: entry.lastAccessAt,
        type,
        driveId,
        timeRange
      });
    }

    return items;
  }

  /**
   * 🎯 新增：综合缓存键与MIME类型推断缓存类型
   */
  private inferCacheType(cacheKey: string, entry: CacheEntry): 'audio' | 'video' | 'media' | 'other' {
    // 优先依据MIME类型判断
    if (entry?.blob?.type && entry.blob.type.startsWith('audio/')) {
      return 'audio';
    }
    // 兼容原有key模式
    if (cacheKey.includes('-decoded-wav')) {
      return 'audio';
    } else if (cacheKey.includes('-video-raw')) {
      return 'video';
    } else if (cacheKey.includes('-media')) {
      return 'media';
    }
    return 'other';
  }

  /**
   * 🎯 新增：解析缓存键信息
   * 改为public以便PreloadScheduler使用
   */
  public parseCacheKeyInfo(cacheKey: string): { driveId?: string; timeRange?: string } {
    const result: { driveId?: string; timeRange?: string } = {};

    // 解析音频缓存键：drive-1-0-300-decoded-wav
    const audioMatch = cacheKey.match(/^([^-]+)-(\d+)-(\d+)-decoded-wav$/);
    if (audioMatch) {
      result.driveId = audioMatch[1];
      result.timeRange = `${audioMatch[2]}-${audioMatch[3]}帧`;
      return result;
    }

    // 解析视频缓存键：drive-1-video-raw
    const videoMatch = cacheKey.match(/^([^-]+)-video-raw$/);
    if (videoMatch) {
      result.driveId = videoMatch[1];
      return result;
    }

    // 解析媒体缓存键：fileId-media
    const mediaMatch = cacheKey.match(/^([^-]+)-media$/);
    if (mediaMatch) {
      result.driveId = mediaMatch[1];
      return result;
    }

    return result;
  }

  /**
   * 🎯 新增：判断是否为视频相关的blob
   * 用于在清理时跳过视频blob，避免误删正在播放的视频
   * 🎯 修复：暂时忽略所有视频元素，不清理任何视频
   */
  private isVideoRelatedBlob(cacheKey: string): boolean {
    // 🎯 修复问题2.3：暂时忽略所有视频相关的blob，不清理任何视频
    // 识别所有可能的视频blob key格式：
    // 1. video-drive-* (从drive下载的视频)
    // 2. video-metadata-* (视频元数据)
    // 3. video-local-* (本地上传的视频)
    // 4. video-jimeng-* (即梦生成的视频)
    // 5. -media (媒体缓存，可能包含视频)
    if (cacheKey.includes('video-drive-')
        || cacheKey.includes('video-metadata-')
        || cacheKey.includes('video-local-')
        || cacheKey.includes('video-jimeng-')
        || cacheKey.includes('-media')) {
      return true; // 保留所有视频相关缓存
    }
    
    // 2. 允许清理 video-raw 缓存（因为现在使用 media 缓存）
    if (cacheKey.includes('-video-raw')) {
      return false; // 允许清理 video-raw 缓存
    }
    
    return false;
  }

  /**
   * 🎯 新增：删除特定缓存项
   */
  async removeCacheItem(cacheKey: string): Promise<boolean> {
    const entry = this.cache.get(cacheKey);
    if (!entry) {
      return false;
    }

    try {
      URL.revokeObjectURL(entry.objectURL);
      this.cache.delete(cacheKey);
      devLog.log(`[BlobUrlCache] 删除缓存项: ${cacheKey}`);
      return true;
    } catch (error) {
      devLog.error(`[BlobUrlCache] 删除缓存项失败: ${cacheKey}`, error);
      return false;
    }
  }

  /**
   * 🎯 新增：手动触发音频缓存清理（用于测试和调试）
   */
  public async triggerAudioCacheCleanup(): Promise<void> {
    const audioCacheItems = this.getAudioCacheItems();
    devLog.log(`[BlobUrlCache] 手动触发音频缓存清理，当前音频缓存项: ${audioCacheItems.length}条`);
    
    if (audioCacheItems.length > 0) {
      await this.performAudioCacheCleanup(audioCacheItems);
    } else {
      devLog.log('[BlobUrlCache] 没有音频缓存项需要清理');
    }
  }

  /**
   * 🎯 新增：触发完整清理检查（供外部调用）
   * 用于在播放暂停或seek时触发清理
   */
  public async triggerCleanupCheck(): Promise<void> {
    devLog.log('[BlobUrlCache] 外部触发清理检查');
    await this.cleanupIfNeeded();
  }

  /**
   * 🎯 新增：获取音频缓存统计信息
   */
  public async getAudioCacheStats(): Promise<{
    totalCount: number;
    beforePlayheadCount: number;
    afterPlayheadCount: number;
    currentPlayhead: number;
  }> {
    const audioItems = this.getAudioCacheItems();
    const currentPlayhead = await this.getCurrentPlayheadFrame();
    
    const beforePlayheadCount = audioItems.filter(item => item.endFrame <= currentPlayhead).length;
    const afterPlayheadCount = audioItems.length - beforePlayheadCount;
    
    return {
      totalCount: audioItems.length,
      beforePlayheadCount,
      afterPlayheadCount,
      currentPlayhead
    };
  }

  /**
   * 🎯 新增：检查音频URL缓存数量，超过25条时触发音频特有的清理机制
   */
  private async checkAudioCacheCleanup(): Promise<void> {
    const audioCacheItems = this.getAudioCacheItems();
    
    if (audioCacheItems.length > 25) {
      devLog.log(`[BlobUrlCache] 音频URL缓存超过25条 (${audioCacheItems.length}条)，触发音频特有清理机制`);
      await this.performAudioCacheCleanup(audioCacheItems);
    }
  }

  /**
   * 🎯 新增：获取所有音频缓存项
   * - 识别标准：
   *   1) 缓存键包含 -decoded-wav（解码产物）
   *   2) 或 Blob 的 MIME 类型为 audio/*（如 RecordPanel 生成的 record-、roll-audio- 等）
   */
  private getAudioCacheItems(): Array<{ key: string; entry: CacheEntry; startFrame: number; endFrame: number }> {
    const audioItems: Array<{ key: string; entry: CacheEntry; startFrame: number; endFrame: number }> = [];
    
    for (const [key, entry] of this.cache) {
      const isAudioByKey = this.isAudioCacheKey(key);
      const isAudioByMime = Boolean(entry?.blob?.type && entry.blob.type.startsWith('audio/'));
      if (isAudioByKey || isAudioByMime) {
        const { startFrame, endFrame } = this.parseAudioCacheKey(key);
        audioItems.push({ key, entry, startFrame, endFrame });
      }
    }
    
    return audioItems;
  }

  /**
   * 🎯 新增：判断是否为音频缓存键
   */
  private isAudioCacheKey(cacheKey: string): boolean {
    return cacheKey.includes('-decoded-wav');
  }

  /**
   * 🎯 新增：解析音频缓存键，提取开始和结束帧数
   */
  private parseAudioCacheKey(cacheKey: string): { startFrame: number; endFrame: number } {
    // 音频缓存键格式：${driveId}-${start}-${end}-decoded-wav
    const match = cacheKey.match(/^[^-]+-(\d+)-(\d+)-decoded-wav$/);
    if (match) {
      return {
        startFrame: parseInt(match[1]),
        endFrame: parseInt(match[2])
      };
    }
    return { startFrame: 0, endFrame: 0 };
  }

  /**
   * 🎯 新增：获取当前playhead位置（帧数）
   */
  private async getCurrentPlayheadFrame(): Promise<number> {
    try {
      // 从playerState获取当前播放帧
      const { usePlayerState } = await import('@/stores/playerState');
      const playerState = usePlayerState();
      return playerState.playStartFrame || 0;
    } catch (error) {
      devLog.warn('[BlobUrlCache] 无法获取playhead位置，使用默认值0', error);
      return 0;
    }
  }

  private revokeAudioRoundEntry(
    item: {
      key: string;
      entry: CacheEntry;
      startFrame: number;
      endFrame: number;
      score?: number;
      lastAccessAt: number;
    },
    deletedKeys: Set<string>,
    roundLabel: 'Round1' | 'Round2'
  ): void {
    if (deletedKeys.has(item.key)) return;
    if (this.protectionCallback?.(item.key)) {
      devLog.log(`[BlobUrlCache] [${roundLabel}] 跳过受保护的音频缓存: ${item.key}`);
      return;
    }
    try {
      URL.revokeObjectURL(item.entry.objectURL);
      this.cache.delete(item.key);
      deletedKeys.add(item.key);
      const tail = roundLabel === 'Round1'
        ? ` (${item.startFrame}-${item.endFrame}帧), score=${item.score}`
        : ` (${item.startFrame}-${item.endFrame}帧), lastAccessAt=${item.lastAccessAt}`;
      devLog.log(`[BlobUrlCache] [${roundLabel}] 清理音频缓存: ${item.key}${tail}`);
    } catch (error) {
      devLog.error(`[BlobUrlCache] [${roundLabel}] 清理音频缓存失败: ${item.key}`, error);
    }
  }

  /**
   * 🎯 新增：执行音频缓存特有的清理机制（两轮清理，最小入侵）
   * 1) 过滤：跳过 AudioPool 在用的 URL（动态导入，实时判断）
   * 2) 第一轮：按距 playhead 距离排序（播放头前×3加权），删除最多5条
   * 3) 第二轮：按 lastAccessAt 升序（最冷），删除最多5条
   */
  private async performAudioCacheCleanup(audioItems: Array<{ key: string; entry: CacheEntry; startFrame: number; endFrame: number }>): Promise<void> {
    try {
      const currentPlayhead = await this.getCurrentPlayheadFrame();
      devLog.log(`[BlobUrlCache] 开始音频缓存清理，当前playhead: ${currentPlayhead}帧，音频缓存项: ${audioItems.length}条`);

      // 动态导入 audioPool，避免静态依赖
      let isUrlInUse: ((url: string) => boolean) | null = null;
      try {
        const mod = await import('@/utils/audioPool');
        isUrlInUse = (mod as any).audioPool?.isUrlInUse?.bind((mod as any).audioPool) || null;
      } catch (e) {
        devLog.warn('[BlobUrlCache] 动态导入 audioPool 失败，跳过在用URL过滤', e);
      }

      const candidates = audioItems.map(item => ({
        key: item.key,
        entry: item.entry,
        startFrame: item.startFrame,
        endFrame: item.endFrame,
        url: item.entry.objectURL,
        lastAccessAt: item.entry.lastAccessAt
      }));

      // 过滤：跳过在用的 URL
      const filtered = candidates.filter(c => {
        if (!isUrlInUse) return true; // 无法检测则保守：不过滤（交由后续策略）
        const inUse = isUrlInUse(c.url);
        if (inUse) {
          devLog.log(`[BlobUrlCache] 跳过在用音频URL: ${c.key}`);
        }
        return !inUse;
      });

      if (filtered.length === 0) {
        devLog.log('[BlobUrlCache] 无可清理的音频缓存（全部在用或为空）');
        return;
      }

      const deletedKeys = new Set<string>();

      // 第一轮：按距离分数删除最多5条
      const withDistance = filtered.map(c => {
        let score = 0;
        if (c.endFrame <= currentPlayhead) {
          // 在播放头前：越远越大，×3 加权
          score = (currentPlayhead - c.endFrame) * 3;
        } else if (c.startFrame >= currentPlayhead) {
          // 在播放头后：越远越大
          score = (c.startFrame - currentPlayhead);
        } else {
          // 跨越/覆盖playhead：认为更重要，score保持0
          score = 0;
        }
        return { ...c, score };
      });

      withDistance.sort((a, b) => b.score - a.score);
      const round1 = withDistance.filter(c => c.score > 0).slice(0, 5);

      for (const item of round1) {
        this.revokeAudioRoundEntry(item, deletedKeys, 'Round1');
      }

      // 第二轮：对剩余候选按lastAccessAt升序，删除最多5条
      const remaining = filtered.filter(c => !deletedKeys.has(c.key));
      if (remaining.length > 0) {
        remaining.sort((a, b) => a.lastAccessAt - b.lastAccessAt);
        const round2 = remaining.slice(0, 5);
        for (const item of round2) {
          this.revokeAudioRoundEntry(item, deletedKeys, 'Round2');
        }
      }

      devLog.log(`[BlobUrlCache] 音频缓存清理完成，共清理 ${deletedKeys.size} 条`);
    } catch (e) {
      devLog.error('[BlobUrlCache] 音频缓存清理流程失败', e);
    }
  }

  /**
   * 检查是否需要清理（带防抖机制）
   * 🎯 修改：只在特定时机触发，并添加2秒防抖
   */
  private async cleanupIfNeeded(): Promise<void> {
    const now = Date.now();
    
    // 防抖检查：如果距离上次清理不足2秒，跳过
    if (now - this.lastCleanupTime < this.cleanupDebounceMs) {
      devLog.log(`[BlobUrlCache] 清理防抖中，跳过本次清理 (距离上次清理: ${now - this.lastCleanupTime}ms)`);
      return;
    }
    
    this.lastCleanupTime = now;
    devLog.log(`[BlobUrlCache] 开始执行清理检查`);
    
    const stats = this.getStats();
    const maxBytes = this.config.ramBudgetMB * 1024 * 1024;
    
    if (stats.totalSize > maxBytes) {
      devLog.log(`[BlobUrlCache] 内存超限，开始清理: ${stats.totalSize} > ${maxBytes}`);
      this.performCleanup();
    }
    
    // 🎯 检查音频URL缓存数量，超过20条时触发音频特有的清理机制
    await this.checkAudioCacheCleanup();
  }

  /**
   * 调度清理（延迟执行）
   */
  private scheduleCleanup(key: ObjectKey): void {
    setTimeout(() => {
      const entry = this.cache.get(key);
      if (entry && entry.refCount <= 0) {
        this.performCleanup();
      }
    }, 1000); // 延迟 1 秒清理
  }

  /**
   * 执行清理
   */
  private performCleanup(): void {
    const now = Date.now();
    const toRemove: ObjectKey[] = [];
    
    // 第一步：找出引用计数为0的条目
    for (const [key, entry] of this.cache) {
      if (entry.refCount <= 0) {
        toRemove.push(key);
      }
    }

    // 第二步：如果内存超限，清理最早访问的且引用计数为0的项（其实第一步已经覆盖了所有refCount<=0的）
    // 但为了保险，我们只清理refCount<=0的。
    // 如果内存依然紧张，我们也无法清理refCount>0的项，因为它们正在被使用。
    // 除非我们引入"弱引用"机制，但Blob URL需要强引用保持有效。
    
    // 之前逻辑是清理过期TTL的，但忽略refCount，这导致在使用中被清理。
    // 现在改为：只清理 refCount <= 0 的项。
    // TTL 逻辑：如果 item 有 refCount > 0，说明还在用，不应该基于 TTL 清理。
    // 如果 item refCount <= 0，它应该被 scheduleCleanup 处理，但这里作为兜底。

    // 视频保护逻辑不再需要，因为只要 VideoTrack 保持引用，refCount 就 > 0。
    
    // 执行清理
    for (const key of toRemove) {
      // 🎯 保护检查：如果被回调保护，则跳过清理
      if (!this.protectionCallback || !this.protectionCallback(key)) {
        const entry = this.cache.get(key);
        if (entry) {
          URL.revokeObjectURL(entry.objectURL);
          this.cache.delete(key);
          devLog.log(`[BlobUrlCache] 清理缓存: ${key}`);
        }
      } else {
        devLog.log(`[BlobUrlCache] 跳过受保护的缓存: ${key}`);
      }
    }
    
    if (toRemove.length > 0) {
      devLog.log(`[BlobUrlCache] 清理完成，移除了 ${toRemove.length} 项`);
    }
    
    // 清理过期的验证缓存
    this.cleanupValidationCache();
  }
}

export const blobUrlCache = new BlobUrlCache();
